import { useEffect, useMemo, useRef } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useLocation } from 'react-router-dom'

import { GET_BANNERS } from '../../services/queries'
import { config } from '../../components/Init/Init'
import l10n from '../../components/i18n/I18N'
import { getPlayerStatusFromStatus } from '../../services/helpers/playerHelper'
import { useGetLobbySlug } from './useGetLobbySlug'
import { usePlayerStore } from '../../services/store'
import { useParseBanners } from './useParseBanners/useParseBanners'
import { isInLobby } from '../../services/utils'
import { useConfigValueByCurrentDevice } from '../useConfigValueByCurrentDevice'

export const useGetLobbyBanners = playerProfileDataReady => {
  const playerStore = usePlayerStore()
  const playerStatus = playerStore.use.status()
  const playerPreviousGames = playerStore.use.previousGames()
  const playerTagsBanner = playerStore.use.tagsBanner()

  const location = useLocation()
  const { parseBanners } = useParseBanners()
  const { lobbySlug } = useGetLobbySlug()
  const bannersRefetchRef = useRef(false)
  const refetchLobbyDataPerDevice = useConfigValueByCurrentDevice(config.refetchLobbyData)

  const skipBannerCMSQuery = useMemo(() => {
    if (getPlayerStatusFromStatus(playerStatus) === 'logged-in') {
      return playerPreviousGames === null || !playerProfileDataReady
    } else {
      return !playerProfileDataReady
    }
  }, [playerPreviousGames, playerProfileDataReady, playerStatus])

  const [loadBannersQuery, { data, refetch, loading }] = useLazyQuery(
    GET_BANNERS(config.cmsVersion, config.bannersVersion),
    {
      fetchPolicy: 'cache-and-network',
      onCompleted: data => parseBanners(data)
    }
  )

  // Fetch banners once ready or on lobby slug change (if ready)
  useEffect(() => {
    if (!skipBannerCMSQuery) {
      loadBannersQuery({
        variables: {
          section: 'lobbies',
          locale: l10n.site,
          slug: lobbySlug
        }
      })
    }
  }, [skipBannerCMSQuery, lobbySlug])

  // Parse banners on playerTagsBanner change
  useEffect(() => {
    if (data) {
      parseBanners(data)
    }
  }, [playerTagsBanner])

  // Refetch banner data on playerTagsBanner change
  useEffect(() => {
    if (data && data.entry && !loading) {
      refetch().then(({ data }) => {
        parseBanners(data)
        console.log('[Casino] Banners refetch completed - "playerTagsBanner" initialized')
      })
    }
  }, [playerTagsBanner])

  // Refetch banner data on location change
  useEffect(() => {
    if (
      isInLobby(location) &&
      data &&
      data.entry &&
      !loading &&
      !bannersRefetchRef.current &&
      refetchLobbyDataPerDevice
    ) {
      refetch().then(({ data }) => {
        parseBanners(data)
        bannersRefetchRef.current = true
        console.log('[Casino] Banners refetch completed')
      })
    }

    if (!isInLobby(location) && bannersRefetchRef.current) {
      bannersRefetchRef.current = false
    }
  }, [location])
}
